/**
 * CourierWorkflowSteps.js - Courier-specific workflow steps for workers
 * Handles: Pickup verification → Route optimization → Multi-stop delivery → Proof of delivery
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MapPin, Package, Camera, Pen, Navigation, Check, X, ChevronRight,
  AlertTriangle, Phone, Clock, Truck, Image, User, FileText, Route,
  ArrowRight, CheckCircle, XCircle, Upload, Loader2, Map
} from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { bookingsAPI, mileageAPI } from '../../services/api';

// Courier workflow steps
const COURIER_STEPS = {
  PICKUP_DETAILS: 'pickup_details',
  NAVIGATE_PICKUP: 'navigate_pickup',
  PICKUP_VERIFICATION: 'pickup_verification',
  PICKUP_PHOTO: 'pickup_photo',
  ROUTE_OVERVIEW: 'route_overview',
  STOP_NAVIGATION: 'stop_navigation',
  STOP_VERIFICATION: 'stop_verification',
  DELIVERY_NAVIGATION: 'delivery_navigation',
  DELIVERY_VERIFICATION: 'delivery_verification',
  DELIVERY_PHOTO: 'delivery_photo',
  SIGNATURE: 'signature',
  COMPLETE: 'complete'
};

const CourierWorkflowSteps = ({
  booking,
  worker,
  onComplete,
  onClose
}) => {
  const [currentStep, setCurrentStep] = useState(COURIER_STEPS.PICKUP_DETAILS);
  const [loading, setLoading] = useState(false);
  
  // Courier info from booking
  const courierInfo = booking?.courier_info || {};
  
  // GPS state
  const [currentLocation, setCurrentLocation] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  
  // Pickup verification state
  const [actualPackageCount, setActualPackageCount] = useState(courierInfo.num_packages || 1);
  const [pickupNotes, setPickupNotes] = useState('');
  const [pickupPhoto, setPickupPhoto] = useState(null);
  const [pickupSignature, setPickupSignature] = useState(null);
  
  // Route optimization state
  const [optimizedRoute, setOptimizedRoute] = useState([]);
  const [currentStopIndex, setCurrentStopIndex] = useState(0);
  const [stopsCompleted, setStopsCompleted] = useState([]);
  
  // Delivery verification state
  const [recipientName, setRecipientName] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [deliveryPhoto, setDeliveryPhoto] = useState(null);
  const [recipientSignature, setRecipientSignature] = useState(null);
  const [recipientUnavailable, setRecipientUnavailable] = useState(false);
  const [unavailableReason, setUnavailableReason] = useState('');
  
  // Signature canvas
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  
  // File input refs
  const pickupPhotoRef = useRef(null);
  const deliveryPhotoRef = useRef(null);
  const stopPhotoRefs = useRef({});

  // Initialize and optimize route
  useEffect(() => {
    if (courierInfo.additional_stops?.length > 0) {
      optimizeRoute();
    }
  }, [courierInfo]);

  // Get current GPS location
  const getCurrentLocation = useCallback(() => {
    return new Promise((resolve, reject) => {
      setGpsLoading(true);
      if (!navigator.geolocation) {
        setGpsLoading(false);
        reject(new Error('GPS not available'));
        return;
      }
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          setCurrentLocation(loc);
          setGpsLoading(false);
          resolve(loc);
        },
        (error) => {
          setGpsLoading(false);
          reject(error);
        },
        { enableHighAccuracy: true, timeout: 15000 }
      );
    });
  }, []);

  // Calculate distance between two ZIP codes
  const calculateDistance = async (zip1, zip2) => {
    try {
      const res = await mileageAPI.calculateDistance(zip1, zip2);
      return res.data?.distance_miles || 0;
    } catch {
      return 0;
    }
  };

  // Optimize route for multi-stop deliveries
  const optimizeRoute = async () => {
    const stops = courierInfo.additional_stops || [];
    if (stops.length === 0) {
      setOptimizedRoute([]);
      return;
    }

    setLoading(true);
    try {
      // Build route points: Pickup → Stops → Final Delivery
      const points = [
        { type: 'pickup', zip: courierInfo.pickup_zip, address: courierInfo.pickup_address, ...courierInfo },
        ...stops.map((stop, idx) => ({ type: 'stop', index: idx, zip: stop.zip, address: stop.address, ...stop })),
        { type: 'delivery', zip: courierInfo.delivery_zip, address: courierInfo.delivery_address, ...courierInfo }
      ];

      // Simple nearest-neighbor optimization
      const optimized = [];
      let current = points[0]; // Start at pickup
      optimized.push(current);
      
      const remaining = points.slice(1, -1); // Stops only (not final delivery)
      const finalDelivery = points[points.length - 1];

      while (remaining.length > 0) {
        let nearestIdx = 0;
        let nearestDist = Infinity;

        for (let i = 0; i < remaining.length; i++) {
          const dist = await calculateDistance(current.zip, remaining[i].zip);
          if (dist < nearestDist) {
            nearestDist = dist;
            nearestIdx = i;
          }
        }

        current = remaining.splice(nearestIdx, 1)[0];
        current.estimatedDistance = nearestDist;
        optimized.push(current);
      }

      // Add final delivery
      const finalDist = await calculateDistance(current.zip, finalDelivery.zip);
      finalDelivery.estimatedDistance = finalDist;
      optimized.push(finalDelivery);

      setOptimizedRoute(optimized);
      toast.success('Route optimized for shortest distance');
    } catch (error) {
      console.error('Route optimization error:', error);
      // Fall back to original order
      setOptimizedRoute([
        { type: 'pickup', ...courierInfo },
        ...stops.map((stop, idx) => ({ type: 'stop', index: idx, ...stop })),
        { type: 'delivery', ...courierInfo }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Handle photo capture
  const handlePhotoCapture = async (event, type, stopIndex = null) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLoading(true);
    try {
      // Create object URL for preview
      const photoUrl = URL.createObjectURL(file);
      
      if (type === 'pickup') {
        setPickupPhoto(photoUrl);
      } else if (type === 'delivery') {
        setDeliveryPhoto(photoUrl);
      } else if (type === 'stop' && stopIndex !== null) {
        // Handle stop photos
        const updatedStops = [...stopsCompleted];
        if (!updatedStops[stopIndex]) {
          updatedStops[stopIndex] = {};
        }
        updatedStops[stopIndex].photo = photoUrl;
        setStopsCompleted(updatedStops);
      }
      
      toast.success('Photo captured');
    } catch (error) {
      toast.error('Failed to capture photo');
    } finally {
      setLoading(false);
    }
  };

  // Signature canvas handlers
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setRecipientSignature(null);
    setPickupSignature(null);
  };

  const saveSignature = (type) => {
    const canvas = canvasRef.current;
    const signatureData = canvas.toDataURL('image/png');
    if (type === 'pickup') {
      setPickupSignature(signatureData);
    } else {
      setRecipientSignature(signatureData);
    }
    toast.success('Signature saved');
  };

  // Verify pickup
  const handleVerifyPickup = async () => {
    setLoading(true);
    try {
      const location = await getCurrentLocation().catch(() => null);
      
      await bookingsAPI.courierVerifyPickup(booking.id, {
        actual_package_count: actualPackageCount,
        pickup_notes: pickupNotes,
        pickup_photo: pickupPhoto,
        pickup_signature: pickupSignature,
        gps_latitude: location?.latitude,
        gps_longitude: location?.longitude
      });

      toast.success('Pickup verified successfully');
      
      // Move to next step
      if (optimizedRoute.length > 2) {
        // Has additional stops
        setCurrentStep(COURIER_STEPS.ROUTE_OVERVIEW);
      } else {
        // Direct to delivery
        setCurrentStep(COURIER_STEPS.DELIVERY_NAVIGATION);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify pickup');
    } finally {
      setLoading(false);
    }
  };

  // Complete a stop
  const handleCompleteStop = async (stopIndex) => {
    setLoading(true);
    try {
      const location = await getCurrentLocation().catch(() => null);
      
      await bookingsAPI.courierUpdateStop(booking.id, {
        stop_index: stopIndex,
        status: 'completed',
        gps_latitude: location?.latitude,
        gps_longitude: location?.longitude,
        notes: stopsCompleted[stopIndex]?.notes || ''
      });

      const updated = [...stopsCompleted];
      updated[stopIndex] = { ...updated[stopIndex], completed: true };
      setStopsCompleted(updated);

      toast.success(`Stop ${stopIndex + 1} completed`);
      
      // Check if more stops or move to final delivery
      const remainingStops = optimizedRoute.filter(
        (r, i) => r.type === 'stop' && !updated[r.index]?.completed
      );
      
      if (remainingStops.length > 0) {
        setCurrentStopIndex(currentStopIndex + 1);
        setCurrentStep(COURIER_STEPS.STOP_NAVIGATION);
      } else {
        setCurrentStep(COURIER_STEPS.DELIVERY_NAVIGATION);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete stop');
    } finally {
      setLoading(false);
    }
  };

  // Verify delivery
  const handleVerifyDelivery = async () => {
    if (!recipientUnavailable && !recipientName) {
      toast.error('Please enter recipient name');
      return;
    }

    if (courierInfo.signature_required && !recipientSignature && !recipientUnavailable) {
      toast.error('Signature is required for this delivery');
      return;
    }

    setLoading(true);
    try {
      const location = await getCurrentLocation().catch(() => null);
      
      await bookingsAPI.courierVerifyDelivery(booking.id, {
        recipient_name_captured: recipientName || 'N/A',
        delivery_notes: deliveryNotes,
        delivery_photo: deliveryPhoto,
        recipient_signature: recipientSignature,
        gps_latitude: location?.latitude,
        gps_longitude: location?.longitude,
        recipient_unavailable: recipientUnavailable,
        unavailable_reason: unavailableReason
      });

      toast.success(recipientUnavailable ? 'Delivery attempt recorded' : 'Delivery verified successfully');
      setCurrentStep(COURIER_STEPS.COMPLETE);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify delivery');
    } finally {
      setLoading(false);
    }
  };

  // Open navigation app
  const openNavigation = (address) => {
    const encodedAddress = encodeURIComponent(address);
    // Try Google Maps first, fall back to Apple Maps
    const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`;
    window.open(googleMapsUrl, '_blank');
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case COURIER_STEPS.PICKUP_DETAILS:
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-6 h-6 text-green-500" />
              <h3 className="text-lg font-bold">Pickup Details</h3>
            </div>
            
            <div className="bg-[var(--background-subtle)] p-4 rounded-lg space-y-3">
              <div>
                <Label className="text-xs text-[var(--text-muted)]">Contact</Label>
                <p className="font-medium">{courierInfo.pickup_contact_name}</p>
                <a href={`tel:${courierInfo.pickup_contact_phone}`} className="text-[var(--primary)] flex items-center gap-1">
                  <Phone className="w-4 h-4" />
                  {courierInfo.pickup_contact_phone}
                </a>
              </div>
              
              <div>
                <Label className="text-xs text-[var(--text-muted)]">Address</Label>
                <p className="font-medium">{courierInfo.pickup_address}</p>
                <p className="text-sm text-[var(--text-muted)]">
                  {courierInfo.pickup_city}, {courierInfo.pickup_state} {courierInfo.pickup_zip}
                </p>
              </div>
              
              {courierInfo.pickup_instructions && (
                <div>
                  <Label className="text-xs text-[var(--text-muted)]">Instructions</Label>
                  <p className="text-sm">{courierInfo.pickup_instructions}</p>
                </div>
              )}
              
              <div>
                <Label className="text-xs text-[var(--text-muted)]">Package</Label>
                <p className="font-medium">{courierInfo.num_packages} x {courierInfo.package_type}</p>
                <p className="text-sm text-[var(--text-muted)]">Size: {courierInfo.package_size}</p>
                {courierInfo.special_handling?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {courierInfo.special_handling.map(h => (
                      <span key={h} className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">
                        {h}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <Button
              onClick={() => setCurrentStep(COURIER_STEPS.NAVIGATE_PICKUP)}
              className="w-full bg-[var(--primary)] text-black"
            >
              <Navigation className="w-4 h-4 mr-2" />
              Navigate to Pickup
            </Button>
          </div>
        );

      case COURIER_STEPS.NAVIGATE_PICKUP:
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Navigation className="w-6 h-6 text-blue-500" />
              <h3 className="text-lg font-bold">Navigate to Pickup</h3>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg text-center">
              <Map className="w-12 h-12 mx-auto mb-3 text-blue-500" />
              <p className="font-medium">{courierInfo.pickup_address}</p>
              <p className="text-sm text-[var(--text-muted)]">
                {courierInfo.pickup_city}, {courierInfo.pickup_state} {courierInfo.pickup_zip}
              </p>
              
              <Button
                onClick={() => openNavigation(`${courierInfo.pickup_address}, ${courierInfo.pickup_city}, ${courierInfo.pickup_state} ${courierInfo.pickup_zip}`)}
                className="mt-4 w-full bg-blue-500 hover:bg-blue-600 text-white"
              >
                <Navigation className="w-4 h-4 mr-2" />
                Open in Maps
              </Button>
            </div>

            <Button
              onClick={async () => {
                setGpsLoading(true);
                try {
                  await getCurrentLocation();
                  setCurrentStep(COURIER_STEPS.PICKUP_VERIFICATION);
                } catch {
                  toast.warning('Could not verify GPS, proceeding anyway');
                  setCurrentStep(COURIER_STEPS.PICKUP_VERIFICATION);
                } finally {
                  setGpsLoading(false);
                }
              }}
              disabled={gpsLoading}
              className="w-full bg-green-500 hover:bg-green-600 text-white"
            >
              {gpsLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              I've Arrived at Pickup
            </Button>
          </div>
        );

      case COURIER_STEPS.PICKUP_VERIFICATION:
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Package className="w-6 h-6 text-green-500" />
              <h3 className="text-lg font-bold">Verify Pickup</h3>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label>Package Count Received *</Label>
                <div className="flex items-center gap-3 mt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setActualPackageCount(Math.max(1, actualPackageCount - 1))}
                  >
                    -
                  </Button>
                  <span className="text-xl font-bold w-12 text-center">{actualPackageCount}</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setActualPackageCount(actualPackageCount + 1)}
                  >
                    +
                  </Button>
                  <span className="text-sm text-[var(--text-muted)]">
                    (Expected: {courierInfo.num_packages})
                  </span>
                </div>
                {actualPackageCount !== courierInfo.num_packages && (
                  <p className="text-amber-600 text-sm mt-1">
                    <AlertTriangle className="w-3 h-3 inline mr-1" />
                    Package count differs from order
                  </p>
                )}
              </div>

              <div>
                <Label>Pickup Notes</Label>
                <Textarea
                  value={pickupNotes}
                  onChange={(e) => setPickupNotes(e.target.value)}
                  placeholder="Any issues or notes about the pickup..."
                  rows={2}
                />
              </div>
            </div>

            <Button
              onClick={() => setCurrentStep(COURIER_STEPS.PICKUP_PHOTO)}
              className="w-full bg-[var(--primary)] text-black"
            >
              <ChevronRight className="w-4 h-4 mr-2" />
              Continue to Photo
            </Button>
          </div>
        );

      case COURIER_STEPS.PICKUP_PHOTO:
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Camera className="w-6 h-6 text-green-500" />
              <h3 className="text-lg font-bold">Pickup Photo</h3>
            </div>
            
            <p className="text-sm text-[var(--text-muted)]">
              Take a photo of the package(s) received for verification
            </p>

            <div className="flex flex-col items-center">
              {pickupPhoto ? (
                <div className="relative w-full max-w-xs">
                  <img src={pickupPhoto} alt="Pickup" className="w-full rounded-lg" />
                  <Button
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => setPickupPhoto(null)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <label className="w-full max-w-xs h-48 border-2 border-dashed border-[var(--border-primary)] rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[var(--primary)] transition-colors">
                  <Camera className="w-12 h-12 text-[var(--text-muted)] mb-2" />
                  <span className="text-sm text-[var(--text-muted)]">Tap to take photo</span>
                  <input
                    ref={pickupPhotoRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => handlePhotoCapture(e, 'pickup')}
                    className="hidden"
                  />
                </label>
              )}
            </div>

            <Button
              onClick={handleVerifyPickup}
              disabled={loading}
              className="w-full bg-green-500 hover:bg-green-600 text-white"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Confirm Pickup Complete
            </Button>
          </div>
        );

      case COURIER_STEPS.ROUTE_OVERVIEW:
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Route className="w-6 h-6 text-blue-500" />
              <h3 className="text-lg font-bold">Optimized Route</h3>
            </div>
            
            <p className="text-sm text-[var(--text-muted)]">
              Your route has been optimized for the shortest distance
            </p>

            <div className="space-y-2">
              {optimizedRoute.map((point, idx) => (
                <div
                  key={idx}
                  className={`p-3 rounded-lg border ${
                    point.type === 'pickup' 
                      ? 'bg-green-50 border-green-200' 
                      : point.type === 'delivery'
                      ? 'bg-red-50 border-red-200'
                      : 'bg-blue-50 border-blue-200'
                  } ${stopsCompleted[point.index]?.completed ? 'opacity-50' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                        point.type === 'pickup' ? 'bg-green-500' :
                        point.type === 'delivery' ? 'bg-red-500' : 'bg-blue-500'
                      }`}>
                        {idx + 1}
                      </div>
                      <div>
                        <p className="font-medium text-sm">
                          {point.type === 'pickup' ? 'Pickup' :
                           point.type === 'delivery' ? 'Final Delivery' :
                           `Stop ${point.index + 1}`}
                        </p>
                        <p className="text-xs text-[var(--text-muted)]">{point.address}</p>
                      </div>
                    </div>
                    {point.estimatedDistance > 0 && (
                      <span className="text-xs text-[var(--text-muted)]">
                        {point.estimatedDistance.toFixed(1)} mi
                      </span>
                    )}
                    {stopsCompleted[point.index]?.completed && (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                  </div>
                </div>
              ))}
            </div>

            <Button
              onClick={() => {
                if (optimizedRoute.length > 2) {
                  setCurrentStopIndex(1); // First stop after pickup
                  setCurrentStep(COURIER_STEPS.STOP_NAVIGATION);
                } else {
                  setCurrentStep(COURIER_STEPS.DELIVERY_NAVIGATION);
                }
              }}
              className="w-full bg-[var(--primary)] text-black"
            >
              <Navigation className="w-4 h-4 mr-2" />
              Start Route
            </Button>
          </div>
        );

      case COURIER_STEPS.STOP_NAVIGATION:
        const currentStop = optimizedRoute[currentStopIndex];
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Navigation className="w-6 h-6 text-blue-500" />
              <h3 className="text-lg font-bold">Stop {currentStop?.index + 1}</h3>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <p className="font-medium">{currentStop?.address}</p>
              {currentStop?.contact_name && (
                <p className="text-sm">Contact: {currentStop.contact_name}</p>
              )}
              {currentStop?.contact_phone && (
                <a href={`tel:${currentStop.contact_phone}`} className="text-[var(--primary)] text-sm flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  {currentStop.contact_phone}
                </a>
              )}
              
              <Button
                onClick={() => openNavigation(currentStop?.address)}
                className="mt-3 w-full bg-blue-500 hover:bg-blue-600 text-white"
              >
                <Navigation className="w-4 h-4 mr-2" />
                Open in Maps
              </Button>
            </div>

            <Button
              onClick={() => setCurrentStep(COURIER_STEPS.STOP_VERIFICATION)}
              className="w-full bg-green-500 hover:bg-green-600 text-white"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              I've Arrived at Stop
            </Button>
          </div>
        );

      case COURIER_STEPS.STOP_VERIFICATION:
        const stopToVerify = optimizedRoute[currentStopIndex];
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Package className="w-6 h-6 text-blue-500" />
              <h3 className="text-lg font-bold">Complete Stop {stopToVerify?.index + 1}</h3>
            </div>
            
            <div>
              <Label>Stop Photo (Optional)</Label>
              <div className="mt-2">
                {stopsCompleted[stopToVerify?.index]?.photo ? (
                  <div className="relative w-full max-w-xs">
                    <img src={stopsCompleted[stopToVerify?.index]?.photo} alt="Stop" className="w-full rounded-lg" />
                    <Button
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => {
                        const updated = [...stopsCompleted];
                        if (updated[stopToVerify?.index]) {
                          updated[stopToVerify.index].photo = null;
                        }
                        setStopsCompleted(updated);
                      }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="w-full h-32 border-2 border-dashed border-[var(--border-primary)] rounded-lg flex flex-col items-center justify-center cursor-pointer">
                    <Camera className="w-8 h-8 text-[var(--text-muted)] mb-1" />
                    <span className="text-xs text-[var(--text-muted)]">Take photo</span>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => handlePhotoCapture(e, 'stop', stopToVerify?.index)}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={stopsCompleted[stopToVerify?.index]?.notes || ''}
                onChange={(e) => {
                  const updated = [...stopsCompleted];
                  if (!updated[stopToVerify?.index]) {
                    updated[stopToVerify.index] = {};
                  }
                  updated[stopToVerify.index].notes = e.target.value;
                  setStopsCompleted(updated);
                }}
                placeholder="Notes for this stop..."
                rows={2}
              />
            </div>

            <Button
              onClick={() => handleCompleteStop(stopToVerify?.index)}
              disabled={loading}
              className="w-full bg-green-500 hover:bg-green-600 text-white"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Complete Stop
            </Button>
          </div>
        );

      case COURIER_STEPS.DELIVERY_NAVIGATION:
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Truck className="w-6 h-6 text-red-500" />
              <h3 className="text-lg font-bold">Final Delivery</h3>
            </div>
            
            <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
              <div className="mb-3">
                <Label className="text-xs text-[var(--text-muted)]">Recipient</Label>
                <p className="font-medium">{courierInfo.recipient_name}</p>
                <a href={`tel:${courierInfo.recipient_phone}`} className="text-[var(--primary)] flex items-center gap-1">
                  <Phone className="w-4 h-4" />
                  {courierInfo.recipient_phone}
                </a>
              </div>
              
              <div className="mb-3">
                <Label className="text-xs text-[var(--text-muted)]">Address</Label>
                <p className="font-medium">{courierInfo.delivery_address}</p>
                <p className="text-sm text-[var(--text-muted)]">
                  {courierInfo.delivery_city}, {courierInfo.delivery_state} {courierInfo.delivery_zip}
                </p>
              </div>
              
              {courierInfo.delivery_instructions && (
                <div className="mb-3">
                  <Label className="text-xs text-[var(--text-muted)]">Instructions</Label>
                  <p className="text-sm">{courierInfo.delivery_instructions}</p>
                </div>
              )}
              
              {courierInfo.signature_required && (
                <div className="flex items-center gap-2 p-2 bg-amber-100 rounded mt-2">
                  <Pen className="w-4 h-4 text-amber-600" />
                  <span className="text-sm text-amber-700 font-medium">Signature Required</span>
                </div>
              )}
              
              <Button
                onClick={() => openNavigation(`${courierInfo.delivery_address}, ${courierInfo.delivery_city}, ${courierInfo.delivery_state} ${courierInfo.delivery_zip}`)}
                className="mt-3 w-full bg-red-500 hover:bg-red-600 text-white"
              >
                <Navigation className="w-4 h-4 mr-2" />
                Navigate to Delivery
              </Button>
            </div>

            <Button
              onClick={async () => {
                await getCurrentLocation().catch(() => null);
                setCurrentStep(COURIER_STEPS.DELIVERY_VERIFICATION);
              }}
              className="w-full bg-green-500 hover:bg-green-600 text-white"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              I've Arrived at Delivery
            </Button>
          </div>
        );

      case COURIER_STEPS.DELIVERY_VERIFICATION:
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Package className="w-6 h-6 text-red-500" />
              <h3 className="text-lg font-bold">Verify Delivery</h3>
            </div>

            {!recipientUnavailable ? (
              <>
                <div>
                  <Label>Recipient Name *</Label>
                  <Input
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                    placeholder="Name of person receiving package"
                  />
                </div>

                <div>
                  <Label>Delivery Notes</Label>
                  <Textarea
                    value={deliveryNotes}
                    onChange={(e) => setDeliveryNotes(e.target.value)}
                    placeholder="Any notes about the delivery..."
                    rows={2}
                  />
                </div>

                <Button
                  onClick={() => setCurrentStep(COURIER_STEPS.DELIVERY_PHOTO)}
                  className="w-full bg-[var(--primary)] text-black"
                >
                  <ChevronRight className="w-4 h-4 mr-2" />
                  Continue to Photo
                </Button>

                <div className="text-center">
                  <button
                    onClick={() => setRecipientUnavailable(true)}
                    className="text-sm text-[var(--text-muted)] underline"
                  >
                    Recipient not available?
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                  <h4 className="font-medium text-amber-700 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Recipient Unavailable
                  </h4>
                  
                  <div className="mt-3">
                    <Label>Reason *</Label>
                    <Textarea
                      value={unavailableReason}
                      onChange={(e) => setUnavailableReason(e.target.value)}
                      placeholder="Explain why delivery could not be completed..."
                      rows={3}
                    />
                  </div>
                </div>

                <Button
                  onClick={handleVerifyDelivery}
                  disabled={loading || !unavailableReason}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 mr-2" />
                  )}
                  Record Failed Delivery Attempt
                </Button>

                <Button
                  variant="outline"
                  onClick={() => setRecipientUnavailable(false)}
                  className="w-full"
                >
                  Cancel - Recipient is Available
                </Button>
              </>
            )}
          </div>
        );

      case COURIER_STEPS.DELIVERY_PHOTO:
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Camera className="w-6 h-6 text-red-500" />
              <h3 className="text-lg font-bold">Proof of Delivery Photo</h3>
            </div>
            
            <p className="text-sm text-[var(--text-muted)]">
              Take a photo showing the package has been delivered
            </p>

            <div className="flex flex-col items-center">
              {deliveryPhoto ? (
                <div className="relative w-full max-w-xs">
                  <img src={deliveryPhoto} alt="Delivery" className="w-full rounded-lg" />
                  <Button
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => setDeliveryPhoto(null)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <label className="w-full max-w-xs h-48 border-2 border-dashed border-[var(--border-primary)] rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[var(--primary)] transition-colors">
                  <Camera className="w-12 h-12 text-[var(--text-muted)] mb-2" />
                  <span className="text-sm text-[var(--text-muted)]">Tap to take photo</span>
                  <input
                    ref={deliveryPhotoRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => handlePhotoCapture(e, 'delivery')}
                    className="hidden"
                  />
                </label>
              )}
            </div>

            {courierInfo.signature_required ? (
              <Button
                onClick={() => setCurrentStep(COURIER_STEPS.SIGNATURE)}
                className="w-full bg-[var(--primary)] text-black"
              >
                <Pen className="w-4 h-4 mr-2" />
                Get Recipient Signature
              </Button>
            ) : (
              <Button
                onClick={handleVerifyDelivery}
                disabled={loading}
                className="w-full bg-green-500 hover:bg-green-600 text-white"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Complete Delivery
              </Button>
            )}
          </div>
        );

      case COURIER_STEPS.SIGNATURE:
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Pen className="w-6 h-6 text-[var(--primary)]" />
              <h3 className="text-lg font-bold">Recipient Signature</h3>
            </div>
            
            <p className="text-sm text-[var(--text-muted)]">
              Have the recipient sign below to confirm delivery
            </p>

            <div className="border-2 border-[var(--border-primary)] rounded-lg overflow-hidden">
              <canvas
                ref={canvasRef}
                width={300}
                height={150}
                className="w-full bg-white touch-none"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={clearSignature}
                className="flex-1"
              >
                Clear
              </Button>
              <Button
                onClick={() => {
                  saveSignature('delivery');
                }}
                className="flex-1 bg-[var(--primary)] text-black"
              >
                Save Signature
              </Button>
            </div>

            {recipientSignature && (
              <Button
                onClick={handleVerifyDelivery}
                disabled={loading}
                className="w-full bg-green-500 hover:bg-green-600 text-white"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Complete Delivery
              </Button>
            )}
          </div>
        );

      case COURIER_STEPS.COMPLETE:
        return (
          <div className="text-center space-y-4">
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto">
              <Check className="w-10 h-10 text-white" />
            </div>
            
            <h3 className="text-xl font-bold">Delivery Complete!</h3>
            <p className="text-[var(--text-muted)]">
              {recipientUnavailable 
                ? 'Delivery attempt has been recorded' 
                : 'Package has been delivered successfully'}
            </p>

            <div className="bg-[var(--background-subtle)] p-4 rounded-lg text-left space-y-2">
              <div className="flex justify-between">
                <span className="text-[var(--text-muted)]">Packages</span>
                <span className="font-medium">{actualPackageCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--text-muted)]">Recipient</span>
                <span className="font-medium">{recipientUnavailable ? 'Unavailable' : recipientName}</span>
              </div>
              {deliveryPhoto && (
                <div className="flex justify-between">
                  <span className="text-[var(--text-muted)]">Photo</span>
                  <span className="text-green-500">Captured</span>
                </div>
              )}
              {recipientSignature && (
                <div className="flex justify-between">
                  <span className="text-[var(--text-muted)]">Signature</span>
                  <span className="text-green-500">Collected</span>
                </div>
              )}
            </div>

            <Button
              onClick={() => onComplete?.()}
              className="w-full bg-[var(--primary)] text-black"
            >
              Finish & Clock Out
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  // Progress indicator
  const getProgress = () => {
    const steps = Object.values(COURIER_STEPS);
    const currentIndex = steps.indexOf(currentStep);
    return Math.round((currentIndex / (steps.length - 1)) * 100);
  };

  if (!booking) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center">
      <motion.div
        initial={{ y: '100%', opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: '100%', opacity: 0 }}
        className="bg-[var(--background-paper)] w-full max-w-md max-h-[90vh] rounded-t-3xl sm:rounded-2xl overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="p-4 border-b border-[var(--border-primary)] flex items-center justify-between">
          <div>
            <h2 className="font-bold">Courier Job</h2>
            <p className="text-xs text-[var(--text-muted)]">{booking.service_name}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-[var(--background-subtle)]">
          <div
            className="h-full bg-[var(--primary)] transition-all duration-300"
            style={{ width: `${getProgress()}%` }}
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              {renderStepContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default CourierWorkflowSteps;
